<template>
    <Tooltip :is-teleported="true">
        <template #trigger="tooltipProps">
            <button
                :id="buttonId"
                :aria-labelledby="
                    tooltipProps['aria-labelledby'] ? `${tooltipProps['aria-labelledby']} ${buttonId}` : undefined
                "
                :popovertarget="tooltipProps['popovertarget'] ? tooltipProps.popovertarget : undefined"
                :aria-disabled="isDisabled ? true : undefined"
                type="button"
                class="rounded-sm flex flex-row items-center focus-visible:outline-offset-2 focus-visible:outline-2 w-max focus-visible:z-20 group p-1.5"
                :class="[variantClasses, isDisabled ? 'cursor-not-allowed' : 'cursor-pointer']"
                @click="handleClick"
            >
                <div class="flex items-center" :class="iconVariantClasses">
                    <StarFilledIcon v-if="props.status === 'Main-Variant'" class="size-4.5" />
                    <PendingIcon v-else-if="props.status === 'Pending'" class="size-4.5 animate-spin" />
                    <StarEmptyIcon v-else-if="props.status === 'Not-Main-Variant'" class="size-4.5" />
                    <RefreshIcon v-else-if="props.status === 'Error'" class="size-4.5" />
                </div>
            </button>
        </template>

        <template #text>
            <div class="w-max">{{ props.disabledExplanation || iconDescription }}</div>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { $t } from '@/plugins/fluent';
import { computed, provide, ref, useId } from 'vue';
import PendingIcon from '../Icon/PendingIcon.vue';
import RefreshIcon from '../Icon/RefreshIcon.vue';
import StarEmptyIcon from '../Icon/StarEmptyIcon.vue';
import StarFilledIcon from '../Icon/StarFilledIcon.vue';
import { PhaseColor } from '../Timeline/Timeline.types.ts';
import Tooltip from '../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';

type MainVariantBadgeButtonProps = {
    status: 'Main-Variant' | 'Not-Main-Variant' | 'Pending' | 'Error';
    disabledExplanation?: string;
    color: Extract<
        PhaseColor,
        | 'External-Event-Category-Project'
        | 'External-Event-Category-Event'
        | 'External-Event-Category-External-Event'
        | 'Project'
        | 'Event'
    >;
};

type MainVariantBadgeButtonEmits = {
    (e: 'click', ev: MouseEvent): void;
};

const props = defineProps<MainVariantBadgeButtonProps>();
const emit = defineEmits<MainVariantBadgeButtonEmits>();

const buttonId = useId();
const isDisabled = computed(() => Boolean(props.disabledExplanation) || props.status === 'Main-Variant');

const variantClasses = computed(() => {
    if (props.disabledExplanation) {
        switch (props.status) {
            case 'Main-Variant':
                return `
                    border border-gray-600 bg-gray-600 text-gray-50
                    focus-visible:outline-gray-400
                `;

            case 'Not-Main-Variant':
                return `
                    border border-gray-300 bg-gray-100 text-gray-500
                    focus-visible:outline-gray-400
                `;

            case 'Pending':
                return `
                    border border-gray-600 bg-gray-600 text-gray-50
                    focus-visible:outline-gray-400
                `;

            case 'Error':
                return `
                    border border-gray-600 bg-gray-600 text-gray-50
                    focus-visible:outline-gray-400
                `;
        }
    }

    switch (props.color) {
        case 'Event':
        case 'External-Event-Category-Event':
            switch (props.status) {
                case 'Main-Variant':
                    return `
                        border border-sky-700 bg-sky-700 text-sky-50
                        focus-visible:outline-sky-700
                    `;

                case 'Not-Main-Variant':
                    return `
                        border border-sky-700 bg-sky-50 text-sky-700
                        hover:bg-sky-700 hover:text-sky-50
                        active:border-sky-900 active:bg-sky-900 active:text-sky-50
                        focus-visible:outline-sky-700
                    `;

                case 'Pending':
                    return `
                        border border-sky-700 bg-sky-700 text-sky-50
                        hover:border-sky-800 hover:bg-sky-800 hover:text-sky-50
                        active:border-sky-900 active:bg-sky-900 active:text-sky-50
                        focus-visible:outline-sky-700
                    `;

                case 'Error':
                    return `
                        border border-red-700 bg-red-700 text-red-50
                        hover:border-red-800 hover:bg-red-800 hover:text-red-50
                        active:border-red-900 active:bg-red-900 active:text-red-50
                        focus-visible:outline-red-700
                    `;
            }
            break;

        case 'Project':
        case 'External-Event-Category-Project':
            switch (props.status) {
                case 'Main-Variant':
                    return `
                        border border-rose-700 bg-rose-700 text-rose-50
                        focus-visible:outline-rose-700
                    `;

                case 'Not-Main-Variant':
                    return `
                        border border-rose-700 bg-rose-50 text-rose-700
                        hover:bg-rose-700 hover:text-rose-50
                        active:border-rose-900 active:bg-rose-900 active:text-rose-50
                        focus-visible:outline-rose-700
                    `;

                case 'Pending':
                    return `
                        border border-rose-700 bg-rose-700 text-rose-50
                        hover:border-rose-800 hover:bg-rose-800 hover:text-rose-50
                        active:border-rose-900 active:bg-rose-900 active:text-rose-50
                        focus-visible:outline-rose-700
                    `;

                case 'Error':
                    return `
                        border border-red-700 bg-red-700 text-red-50
                        hover:border-red-800 hover:bg-red-800 hover:text-red-50
                        active:border-red-900 active:bg-red-900 active:text-red-50
                        focus-visible:outline-red-700
                    `;
            }
            break;

        case 'External-Event-Category-External-Event':
            switch (props.status) {
                case 'Main-Variant':
                    return `
                        border border-green-700 bg-green-700 text-green-50
                        focus-visible:outline-green-700
                    `;

                case 'Not-Main-Variant':
                    return `
                        border border-green-700 bg-green-50 text-green-700
                        hover:bg-green-700 hover:text-green-50
                        active:border-green-900 active:bg-green-900 active:text-green-50
                        focus-visible:outline-green-700
                    `;

                case 'Pending':
                    return `
                        border border-green-700 bg-green-700 text-green-50
                        hover:border-green-800 hover:bg-green-800 hover:text-green-50
                        active:border-green-900 active:bg-green-900 active:text-green-50
                        focus-visible:outline-green-700
                    `;

                case 'Error':
                    return `
                        border border-red-700 bg-red-700 text-red-50
                        hover:border-red-800 hover:bg-red-800 hover:text-red-50
                        active:border-red-900 active:bg-red-900 active:text-red-50
                        focus-visible:outline-red-700
                    `;
            }
            break;
    }
});

const iconVariantClasses = computed(() => {
    if (props.disabledExplanation) {
        switch (props.status) {
            case 'Main-Variant':
                return `
                    text-gray-400
                `;

            case 'Not-Main-Variant':
                return `
                    text-gray-500
                `;

            case 'Pending':
                return `
                    text-gray-400
                `;

            case 'Error':
                return `
                    text-gray-400
                `;
        }
    }

    switch (props.color) {
        case 'Event':
        case 'External-Event-Category-Event':
            switch (props.status) {
                case 'Main-Variant':
                    return `
                        text-sky-50
                    `;

                case 'Not-Main-Variant':
                    return `
                        group-active:text-sky-50 text-sky-700 group-hover:text-sky-50
                    `;

                case 'Pending':
                    return `
                        text-sky-50 group-hover:text-sky-50
                    `;

                case 'Error':
                    return `
                        text-red-50 group-hover:text-red-50
                    `;
            }
            break;

        case 'Project':
        case 'External-Event-Category-Project':
            switch (props.status) {
                case 'Main-Variant':
                    return `
                        text-rose-50
                    `;

                case 'Not-Main-Variant':
                    return `
                        group-active:text-rose-50 text-rose-700 group-hover:text-rose-50
                    `;

                case 'Pending':
                    return `
                        text-rose-50 group-hover:text-rose-50
                    `;

                case 'Error':
                    return `
                        text-red-50 group-hover:text-red-50
                    `;
            }
            break;

        case 'External-Event-Category-External-Event':
            switch (props.status) {
                case 'Main-Variant':
                    return `
                        text-green-50
                    `;

                case 'Not-Main-Variant':
                    return `
                        group-active:text-green-50 text-green-700 group-hover:text-green-50
                    `;

                case 'Pending':
                    return `
                        text-green-50 group-hover:text-green-50
                    `;

                case 'Error':
                    return `
                        text-red-50 group-hover:text-red-50
                    `;
            }
            break;
    }
});

const iconDescription = computed(() => {
    switch (props.status) {
        case 'Main-Variant':
            return $t('main-variant-badge-is-already-main-variant');
        case 'Not-Main-Variant':
            return $t('main-variant-badge-promote-to-main-variant');
        case 'Pending':
            return $t('main-variant-badge-promote-to-main-variant-pending');
        case 'Error':
            return $t('main-variant-badge-promote-to-main-variant-failed');
    }
});

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

function handleClick(event: MouseEvent) {
    triggerHideTooltip();

    if (isDisabled.value) {
        event.preventDefault();
    } else {
        emit('click', event);
    }
}
</script>
